import { Outlet } from 'react-router-dom'
import { useEffect, useState } from 'react'
import Sidebar from './Sidebar'
import OnboardingTutorial from './OnboardingTutorial'
import PageTransition from './PageTransition'
import { getDataAPI, getEffectiveUserId, postDataAPI } from '../lib/api'
import { makeCacheKey, writeSessionCache } from '../lib/sessionCache'
import { normalizeBookingsData } from '../lib/normalizeBookings'

export default function Layout() {
  const [isCollapsed, setIsCollapsed] = useState(() => {
    const saved = localStorage.getItem('sidebarCollapsed')
    return saved === 'true'
  })

  useEffect(() => {
    // Background prefetch so page navigation never blocks on "Loading…" screens.
    // We write to BOTH sessionStorage (for legacy consumers) AND localStorage
    // (for instant hydration on page mount).
    const run = async () => {
      const userId = getEffectiveUserId() || 'self'
      const cache = (endpoint: string, data: unknown) => {
        writeSessionCache(makeCacheKey([userId, 'api', endpoint]), data)
      }

      const safeGet = async (endpoint: string) => {
        try {
          const data = await getDataAPI(endpoint)
          cache(endpoint, data)
          return data
        } catch {
          return null
        }
      }

      // ── Prefetch bookings & write to localStorage so ShowsPage hydrates instantly ──
      const prefetchBookings = async () => {
        const data = await safeGet('user/getBookings')
        if (data) {
          try {
            const normalized = normalizeBookingsData(data)
            if (normalized.length > 0) {
              const key = `showsPageCache:${userId}`
              // Only write if there isn't already a cache (don't overwrite with stale
              // prefetch if the user just saved new data on ShowsPage)
              const existing = localStorage.getItem(key)
              if (!existing) {
                localStorage.setItem(key, JSON.stringify({ timestamp: Date.now(), shows: normalized }))
              }
            }
          } catch { /* ignore */ }
        }
      }

      // ── Prefetch team members & write to localStorage ──
      const prefetchTeam = async () => {
        try {
          const raw = localStorage.getItem('dizeeUser')
          const user = raw ? JSON.parse(raw) : null
          if (!user?._id) return

          const token = (() => {
            try {
              const t = localStorage.getItem('dizeeToken')
              if (!t) return null
              const parsed = JSON.parse(t)
              return typeof parsed === 'string' ? parsed : parsed
            } catch { return localStorage.getItem('dizeeToken') }
          })()
          if (!token) return

          const res = await postDataAPI(
            'team/members',
            { userId: user._id, userRole: user.professionRole },
            token,
          )
          const data = res?.data?.data ?? res?.data ?? res
          const members = Array.isArray(data) ? data : []
          if (members.length > 0) {
            const key = `teamMembersCache:${userId}`
            if (!localStorage.getItem(key)) {
              localStorage.setItem(key, JSON.stringify({ ts: Date.now(), members }))
            }
          }
        } catch { /* ignore */ }
      }

      // Core navigation surfaces — run in parallel
      await Promise.all([
        prefetchBookings(),
        prefetchTeam(),
        safeGet('tasks'),
        safeGet('orders'),
        safeGet('financials'),
        safeGet('user/getCatalog'),
        safeGet('user/connectors/googleCalendar/status'),
        safeGet('user/connectors/googleCalendar/events?maxResults=50'),
        safeGet('user/connectors/googleCalendar/events?maxResults=250'),
      ])
    }

    // Defer to avoid competing with initial route fetches
    const t = window.setTimeout(() => void run(), 250)
    return () => window.clearTimeout(t)
  }, [])

  const handleToggleCollapse = () => {
    const newState = !isCollapsed
    setIsCollapsed(newState)
    localStorage.setItem('sidebarCollapsed', String(newState))
  }

  return (
    <div className="flex min-h-screen bg-transparent">
      <OnboardingTutorial />
      <Sidebar isCollapsed={isCollapsed} onToggleCollapse={handleToggleCollapse} />
      <main
        className={`flex-1 min-h-0 overflow-y-auto overflow-x-hidden bg-transparent ml-0 ${isCollapsed ? 'md:ml-20' : 'md:ml-72'}`}
        style={{ transition: 'margin-left 200ms ease-in-out' }}
      >
        <div className="min-h-screen">
          <PageTransition>
            <Outlet />
          </PageTransition>
        </div>
      </main>
    </div>
  )
}
